<template>
  <v-card-text class="pa-2" v-if="isLoaded">
    <v-row no-gutters>
      <v-col>
        <app-search :search="ipMapping.search" v-model="ipMapping.filter" />
      </v-col>

      <div class="d-flex flex-row ga-1 flex-wrap ml-2">
        <v-btn
          v-permission.config
          color="primary"
          @click="showIpMappingRegister"
          >追加
        </v-btn>
        <v-btn v-permission.config color="primary" @click="deleteIpMapping"
          >削除
        </v-btn>
      </div>
    </v-row>
    <v-alert
      v-if="!!ipMappingAlertMsg"
      class="my-2"
      :text="ipMappingAlertMsg"
    />

    <div class="d-flex flex-row justify-end mb-2">
      <v-btn color="primary" @click="load">一覧更新</v-btn>
    </div>
    <app-table
      v-model:table-sort-by="ipMapping.sortBy"
      primary-key="virtualIpAddressSeq"
      :sort-null-last="true"
      :fields="ipMapping.fields"
      :items="items"
      :sort-desc="false"
      :filter="ipMapping.filter"
      :filter-option="ipMapping.filter_option"
      :selectable="selectable"
      :hover="true"
      @click:row="showIpMappingReference"
      :td-class="cellClass"
      sticky-header="50vh"
    >
      <template #cell(_selected)="data">
        <v-checkbox-btn
          v-if="!data.item.isPolicyInUse"
          v-permission.config
          :value="data.item"
          v-model="ipMapping.selected"
          @click.stop
        />
      </template>
      <template #cell(dstZone)="data">
        {{ $filter.emptyTo(data.value, "-") }}
      </template>
      <template #cell(isPortForward)="data">
        {{ $filter.enumTo(data.value, "enable") }}
      </template>
      <template #cell(description)="data">
        {{ $filter.emptyTo(data.value, "-") }}
      </template>
      <template #cell(isPolicyInUse)="data">
        {{ $filter.enumTo(data.value, "isPolicyInUse") }}
      </template>
    </app-table>
  </v-card-text>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import type { IpMapping, IpMappings } from "@/apis/IpMappingApi";
import type {
  Type1ExtraInfo,
  Type1PrivateInfo,
  Type1Zone,
} from "@/apis/Type1SiteApi";
import type { ZoneRelationItem } from "@/apis/ZoneRelationApi";
import IpMappingReference from "@/modals/applianceObject/IpMappingReference.vue";
import IpMappingType1Edit from "@/modals/applianceObject/IpMappingType1Edit.vue";
import type { SearchOption } from "@/components/AppSearch.vue";

export type Type1IpMappingItem = IpMappings["ipMappingList"][0] & {
  dstZone: {
    type: "EXTRA" | "PRIVATE";
    zone: Type1Zone;
  } | null;
};

export default defineComponent({
  name: "Type1IpMapping",
  data() {
    return {
      ipMappingAlertMsg: "",
      ipMapping: {
        sortBy: { key: "ipMappingName", order: "asc" } as SortItem,
        fields: [
          {
            key: "ipMappingName",
            title: "名前",
            width: "200px",
            sortable: true,
          },
          {
            key: "globalIpAddress",
            title: "グローバルIP",
            width: "200px",
            sortable: true,
          },
          {
            key: "dstZone",
            title: "宛先ゾーン",
            width: "200px",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (
              value: Type1IpMappingItem["dstZone"]
            ): string | null => {
              if (value) {
                return value.zone.zoneName;
              } else {
                return null;
              }
            },
          },
          {
            key: "privateIpAddress",
            title: "プライベートIP",
            width: "200px",
            sortable: true,
          },
          {
            key: "isPortForward",
            title: "ポートフォワード",
            width: "170px",
            sortable: true,
          },
          {
            key: "description",
            title: "備考",
            minWidth: "100px",
            sortable: true,
          },
          {
            key: "isPolicyInUse",
            title: "ポリシー使用有無",
            width: "170px",
            sortable: true,
          },
        ] as AppTableHeader<IpMapping>[],
        selected: [] as IpMapping[],
        search: {
          ipMappingName: { type: "text", label: "名前" },
          globalIpAddress: { type: "text", label: "グローバルIP" },
          dstZone: { type: "text", label: "宛先ゾーン" },
          privateIpAddress: { type: "text", label: "プライベートIP" },
          isPortForward: {
            type: "select",
            label: "ポートフォワード",
            items: [
              { value: true, text: "有効" },
              { value: false, text: "無効" },
            ],
          },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          ipMappingName: "like",
          globalIpAddress: "like",
          dstZone: "like",
          privateIpAddress: "like",
          isPortForward: "equal",
        } as FilterOption,
      },
      zoneList: [] as ZoneRelationItem[],
      type1IpMapping: {} as IpMappings,
      type1Extra: {} as Type1ExtraInfo,
      type1Private: {} as Type1PrivateInfo,
      isLoaded: false,
    };
  },
  computed: {
    /** true: 一覧選択可能, false: 不可 */
    selectable(): boolean {
      return this.$service.permission.hasAuthority("CONFIG");
    },
    /** テーブル一覧 */
    items(): Type1IpMappingItem[] {
      const extraAddressById = this.type1Extra.siteList
        .flatMap((e) => e.addressList)
        .toMap((e) => e.ipAddressId);
      const privateZoneById = this.type1Private.zoneList.toMap((e) => e.zoneId);
      const privateAddressById = this.type1Private.lineList
        .flatMap((e1) => e1.siteList.flatMap((e2) => e2.addressList))
        .toMap((e) => e.ipAddressId);

      // バックエンドのバグ、サイト内にアドレスが重複して設定された場合、
      // 後に追加したアドレスのゾーンに宛先ゾーンが変わってしまう またはゾーン未配置になることがある。
      // そのためIPマッピングで設定されたアドレスがサイト内で重複して存在する場合、宛先ゾーンにnullを返却
      return this.type1IpMapping.ipMappingList.map((e) => {
        const duplicationCnt =
          Object.values(extraAddressById).filter(
            (e1) => e1.ipAddress === e.privateIpAddress
          ).length +
          Object.values(privateAddressById).filter(
            (e1) => e1.ipAddress === e.privateIpAddress
          ).length;
        // エクストラサイトとプライベートサイトに同じアドレスが複数存在する場合は宛先ゾーンをnull
        if (duplicationCnt > 1) {
          return {
            ...e,
            dstZone: null,
          };
        } else {
          // プライベートIPアドレスがエクストラとプライベートのどちらに所属しているか確認してマッピング
          // エクストラ/プライベートサイトにアドレスがひとつのみ存在する場合、宛先ゾーンを返却
          if (extraAddressById[e.ipAddressId]) {
            return {
              ...e,
              dstZone: { type: "EXTRA", zone: this.type1Extra.zone! },
            };
          } else {
            const zoneId = privateAddressById[e.ipAddressId]!.zoneId!;
            return {
              ...e,
              dstZone: { type: "PRIVATE", zone: privateZoneById[zoneId] },
            };
          }
        }
      });
    },
    /** true: グローバル → エクストラ or プライベートのゾーン接続が存在する, false: 存在しない */
    hasZone(): boolean {
      return this.zoneList.some(
        (e) =>
          e.srcZoneType === "INTERNET" &&
          (e.dstZoneType === "EXTRA" || e.dstZoneType === "PRIVATE")
      );
    },
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    async load() {
      this.zoneList = (
        await this.$api.zoneRelation.getZoneRelation()
      ).zoneRelationList;
      this.type1IpMapping = await this.$api.ipMapping.getType1IpMappingList();
      this.type1Extra = await this.$api.type1SiteZone.getType1ExtraList();
      this.type1Private = await this.$api.type1SiteZone.getType1PrivateList();

      // チェックボックスの状態はデータを再取得しても残るので、ここでクリアしておく
      if (this.ipMapping.selected.length) {
        this.ipMapping.selected = [];
      }
    },

    /**
     * IPマッピング参照モーダルを表示する
     * @param row テーブルで選択された行データ
     */
    async showIpMappingReference(row: Type1IpMappingItem) {
      this.ipMappingAlertMsg = "";
      await this.$modal.show(IpMappingReference, {
        ipMapping: row,
        ipMappingKeyId: this.type1IpMapping.ipMappingKeyId,
      });
      // 各タブの情報を再取得する
      this.$emit("reload");
    },

    /** IPマッピング追加モーダルを表示する */
    async showIpMappingRegister() {
      if (!this.hasZone) {
        this.ipMappingAlertMsg =
          "ゾーン接続設定後に、IPマッピング設定を行ってください。";
        return;
      } else {
        this.ipMappingAlertMsg = "";
      }
      await this.$modal.show(IpMappingType1Edit, {
        ipMappingKeyId: this.type1IpMapping.ipMappingKeyId,
      });
      // 各タブの情報を再取得する
      this.$emit("reload");
    },

    /** IPマッピング削除確認モーダルを表示する */
    async deleteIpMapping() {
      // チェックボックスが選択1つも選択されていない場合、アラート表示して処理終了
      if (this.ipMapping.selected.length <= 0) {
        // アラートを表示する
        this.ipMappingAlertMsg = this.$filter.message("no_select_data");
        return;
      } else {
        // アラートを非表示にする
        this.ipMappingAlertMsg = "";
      }

      await this.$confirm(
        `IPマッピングを${this.ipMapping.selected.length}件削除します。よろしいですか？`
      );
      await this.$api.ipMapping.deleteType1IpMapping({
        ipMappingList: this.ipMapping.selected.map(
          (e: IpMapping) => e.virtualIpAddressSeq
        ),
        ipMappingKeyId: this.type1IpMapping.ipMappingKeyId,
        note: null,
      });
      // 各タブの情報を再取得する
      this.$emit("reload");
    },

    /** エラーメッセージを初期化する(親画面から呼び出される) */
    async alertClear() {
      this.ipMappingAlertMsg = "";
    },

    /** tdにクラスをつける */
    cellClass(field: AppTableHeader<unknown>): string[] | undefined {
      if (field.key === "description") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
  },
});
</script>
